import { trackEvent } from "./api/tracker";

export function getPaintTimings() {
  const ret: Record<string, number> = {};

  if (window.performance?.getEntriesByType) {
    const { performance } = window;
    const performanceEntries = performance.getEntriesByType("paint");
    performanceEntries.forEach((performanceEntry) => {
      const cameledName = performanceEntry.name.replace(/-[a-z]/g, (val) =>
        val.slice(1).toUpperCase()
      );
      ret[cameledName] = performanceEntry.startTime;
    });
  }

  return ret;
}

export function getNavType() {
  try {
    const navigationTiming = performance.getEntriesByType(
      "navigation"
    )[0] as PerformanceNavigationTiming;
    return navigationTiming.type;
  } catch (e) {
    /* NOP */
    return undefined;
  }
}

export function isNavShortened() {
  const { barTime } = window as any;
  return barTime != null;
}

export function navTimeoutDuration(timeout: number): number {
  const { barTime } = window as any;
  return barTime != null ? barTime : timeout;
}

export function setNavTimeout(cb: () => void, timeout: number) {
  const id = setTimeout(cb, navTimeoutDuration(timeout));
  return () => clearTimeout(id);
}

export function awaitNavTimeout(timeout: number) {
  return new Promise<void>((resolve) => setNavTimeout(resolve, timeout));
}

export function perfTrack() {
  const perfData = {
    firstByte: -1,
    domContentLoaded: -1,
    firstContentfulPaint: -1,
    serverContextLoaded: -1,
  };
  // Guard against unsupported browsers or browsers using an older version of the API
  if (!window.performance || !window.performance.getEntries) {
    trackEvent("OnPerfApiUnavailable");
    return Promise.resolve();
  }

  // Grab nav timings -- dom content loaded and calculate time to first byte
  const navEntries = window.performance.getEntriesByType(
    "navigation"
  ) as PerformanceNavigationTiming[];
  const firstNav = navEntries?.[0];
  if (firstNav) {
    perfData.domContentLoaded = firstNav.domContentLoadedEventEnd;
    perfData.firstByte = firstNav.responseStart - firstNav.requestStart;
  }

  // Grab FCP (first contentful paint) -- this is when the user starts seeing some stuff
  const fcpEntries = window.performance.getEntriesByName(
    "first-contentful-paint"
  ) as PerformancePaintTiming[];
  perfData.firstContentfulPaint = fcpEntries?.[0]?.startTime || -1;

  // We can also check how long it took to load server context by finding the associated fetch
  const xhrEntries = window.performance.getEntriesByType(
    "resource"
  ) as PerformanceResourceTiming[];
  const serverContextEntries = xhrEntries?.filter((entry) =>
    entry.name.includes("/api/context/v2")
  );
  perfData.serverContextLoaded = serverContextEntries?.[0]?.responseEnd;

  trackEvent("OnPerfMetricsAvailable", perfData);
  return Promise.resolve();
}
